import axios, { type AxiosInstance } from 'axios'

import { env } from '@/env'
import { serviceUtil } from '@/lib/utils/service-util'
import { ChatRequest, ChatResponse } from '@/types/generated/chat_api'

declare module 'axios' {
  interface AxiosRequestConfig {
    _responseDecoder?: (bytes: Uint8Array) => unknown
  }
  interface InternalAxiosRequestConfig {
    _responseDecoder?: (bytes: Uint8Array) => unknown
  }
}

const createAiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.NEXT_PUBLIC_AI_URL,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    responseType: 'arraybuffer',
  })

  client.interceptors.request.use(async (config) => {
    const { method, data = {} } = config
    config._skipRedirect = true

    const isAlreadyBinary =
      data instanceof ArrayBuffer ||
      Buffer.isBuffer(data) ||
      data instanceof Uint8Array ||
      config._retry

    const validToken = await serviceUtil.ensureTokenValid({ skipRedirect: true })

    if (validToken && config.headers) {
      config.headers.Authorization = `Bearer ${validToken}`
    } else if (!validToken) {
      return Promise.reject(new Error('Unauthorized: no valid token'))
    }

    const isJson = config.headers?.['Content-Type'] === 'application/json'

    if (method?.toLowerCase() !== 'get' && !isAlreadyBinary && !isJson) {
      const request = ChatRequest.create(data)
      if (request.sessionId && config.headers) {
        config.headers['x-session-id'] = request.sessionId
      }
      const binaryData = ChatRequest.encode(request).finish()
      config.data = binaryData
      config.headers['Content-Type'] = 'application/octet-stream'
    } else if (isJson) {
      config.data = JSON.stringify(data)
    }
    config.transformRequest = [(data) => data]

    return config
  })

  client.interceptors.response.use(
    (response) => {
      const isJsonResponse = response.config.responseType === 'json'

      if (
        !isJsonResponse &&
        ((response.config.responseType === 'arraybuffer' && Buffer.isBuffer(response.data)) ||
          response.data instanceof ArrayBuffer)
      ) {
        try {
          const bytes = new Uint8Array(response.data)
          const decoder = response.config._responseDecoder
          response.data = decoder ? decoder(bytes) : ChatResponse.decode(bytes)
        } catch (e) {
          console.error('[ai-client] Response decoding failed', e)
          return Promise.reject({
            status: 500,
            message: 'AI response decoding failed',
          })
        }
      }

      // if (env.NEXT_PUBLIC_ENV !== 'prod') {
      //   console.log('[ai-client.ts Line:72]', response.data)
      // }
      return response
    },
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject({ status: 500, message: 'Unknown AI client error' })
      }

      try {
        return await serviceUtil.handleAxiosResponseErrorInterceptor(error, client, error.config)
      } catch (handledError) {
        return Promise.reject(handledError)
      }
    }
  )

  return client
}

export const orderXAiClient = createAiClient()
